import { createEntityAdapter } from '@reduxjs/toolkit';

import type {
  Flow,
  FlowFunction,
  Step,
  TransitionFunctionReference,
} from '@polyai/stream/store/reducers/flows/types';
import {
  createReferenceAdapter,
  type References,
} from '@polyai/stream/store/references';

export const flowsAdapter = createEntityAdapter<Flow>();
export const flowStepsAdapter = createEntityAdapter<Step>();
export const transitionFunctionsAdapter = createEntityAdapter<FlowFunction>();

/**
 * Reference adapter for transition functions
 * Provides initial state for flowSteps and variables references
 */

const transitionFunctionReferences: TransitionFunctionReference[] = [
  'flowSteps',
  'variables',
];

export const transitionFunctionRefAdapter = createReferenceAdapter(
  transitionFunctionReferences,
);

/**
 * An object containing reference mappings used for different step types.
 * Adding new step types requires updating this object.
 * The resulting adapters are generated automatically
 */
const references = {
  advanced_step: [
    'sms',
    'handoff',
    'attributes',
    'transitionFunctions',
    'globalFunctions',
    'entities',
    'variables',
  ],
  default_step: ['attributes', 'entities', 'variables', 'extractedEntities'],
  function_step: [],
} as const satisfies Record<Step['type'], readonly string[]>;

// Utility type to convert snake_case to camelCase
type SnakeToCamel<S extends string> = S extends `${infer Head}_${infer Tail}`
  ? `${Head}${Capitalize<SnakeToCamel<Tail>>}`
  : S;

type ReferenceAdapters = {
  [K in Step['type'] as `${SnakeToCamel<K>}RefAdapter`]: ReturnType<
    typeof createReferenceAdapter<
      (typeof references)[K][number],
      { references?: References<(typeof references)[K][number]> },
      string
    >
  >;
};

const adapters: ReferenceAdapters = Object.entries(references).reduce(
  (prev, [key, value]) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );
    return {
      ...prev,
      [`${camelKey}RefAdapter`]: createReferenceAdapter(value),
    };
  },
  {} as ReferenceAdapters,
);

// make sure to re-export here if you add a new step type
export const { advancedStepRefAdapter, defaultStepRefAdapter } = adapters;
